import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { getUid } from '@syncfusion/ej2-angular-grids';
import { CloseEventArgs, DialogComponent } from '@syncfusion/ej2-angular-popups';
import { catchError, finalize, Observable, of, Subject, switchMap, take, tap } from 'rxjs';
import { ExameNaturezaDto } from 'src/app/dtos/exames/exame-natureza-dto';
import { PessoaExamePeriodicidadeConsultaDto, PessoaExamePeriodicidadeInsercaoDto } from 'src/app/dtos/periodicidades/pessoa-exame-periodicidade';
import { SelectDataSource } from 'src/app/libs/ax-select/select-data-source';
import { validIntId } from 'src/app/libs/ax-validators/ax-validators';
import { ToastService } from 'src/app/libs/toast/toast.service';
import { ExameNaturezaService } from 'src/app/servicos/exame-natureza.service';
import { ExamePeriodicidadeService } from 'src/app/servicos/exame-periodicidade.service';
import { PacienteService } from 'src/app/servicos/paciente.service';
import { PessoaExamePeriodicidadeService } from 'src/app/servicos/pessoa-exame-periodicidade.service';


interface ExamePeriodicidadeForm {
  id: FormControl<number>;
  pessoaId: FormControl<number>;
  pessoaNome: FormControl<string>;
  exameNaturezaId: FormControl<number>;
  exameNaturezaTexto: FormControl<string>;
  periodicidadeId: FormControl<number>;
  periodicidadeTexto: FormControl<string>;
  motivo: FormControl<string>;
  ativo: FormControl<boolean>;
}

@Component({
  selector: 'app-pessoa-exame-periodicidade-cadastro',
  templateUrl: './pessoa-exame-periodicidade-cadastro.component.html',
  styleUrls: ['./pessoa-exame-periodicidade-cadastro.component.less']
})
export class PessoaExamePeriodicidadeCadastroComponent implements OnInit {
  crudNome: string = PessoaExamePeriodicidadeCadastroComponent.name;

  dsPeriodicidades: SelectDataSource = this.periodicidadeService.dsSelecaoPeriodicidades();
  
  @ViewChild('modal', { static: true })
  modal!: DialogComponent;

  id: string = getUid("id");

  submetido: boolean = false;
  emOperacao: boolean = false;

  tabOk: boolean = false;

  alteracaoExameLiberada: boolean = false;

  @Input()
  apenasConsulta: boolean = false;

  modelo: FormGroup<ExamePeriodicidadeForm> = this.fb.group<ExamePeriodicidadeForm>({
    id: this.fb.nonNullable.control<number>(0, [ validIntId ]),
    pessoaId: this.fb.nonNullable.control<number>(0),
    pessoaNome: this.fb.nonNullable.control<string>(''),
    exameNaturezaId: this.fb.nonNullable.control<number>(0),
    exameNaturezaTexto: this.fb.nonNullable.control<string>('', [ Validators.required ]),
    periodicidadeId: this.fb.nonNullable.control<number>(0, [ validIntId ]),
    periodicidadeTexto: this.fb.nonNullable.control<string>(''),
    motivo: this.fb.nonNullable.control<string>(''),
    ativo: this.fb.nonNullable.control<boolean>(true)
  });

  assuntoCadastro: Subject<number | null> = new Subject<number | null>();

  constructor(private fb: FormBuilder, private toast: ToastService,
    private pessoaExamePeriodicidadeService: PessoaExamePeriodicidadeService,
    private exameNaturezaService: ExameNaturezaService,
    private periodicidadeService: ExamePeriodicidadeService,
    private pacienteService: PacienteService) { }

  ngOnInit(): void {
  }

  abrirConsultaOuEdicao(id: number) {
    this.submetido = false;
    this.modal.show();

    return new Observable<number | null>((subscriber) => {
      let subs = this.assuntoCadastro
      .pipe(
        take(1)
      )
      .subscribe((val) => {
        subscriber.next(val);
        subscriber.complete();
        subs.unsubscribe();
      });

      this.executarAberturaEmModoConsultaEdicao(id);

      return { unsubscribe: () => subs.unsubscribe() };
    });
  }

  abrirInsercao(exameNaturezaId: number | null) {
    this.submetido = false;
    this.modal.show();

    return new Observable<number | null>((subscriber) => {
      let subs = this.assuntoCadastro
      .pipe(
        take(1)
      )
      .subscribe((val) => {
        subscriber.next(val);
        subscriber.complete();
        subs.unsubscribe();
      });

      this.executarAberturaEmModoInsercao(exameNaturezaId);

      return { unsubscribe: () => subs.unsubscribe() };
    });
  }

  executarAberturaEmModoConsultaEdicao(id: number) {
    this.emOperacao = true;
    this.alteracaoExameLiberada = false;

    this.pessoaExamePeriodicidadeService
    .consultar(id)
    .pipe(
      finalize(() => {
        this.emOperacao = false;
      })
    )
    .subscribe((dto) => {
      this.carregar(dto);
    });
  }

  executarAberturaEmModoInsercao(exameNaturezaId: number | null) {
    this.emOperacao = true;
    this.alteracaoExameLiberada = true;

    let exame$ = new Observable<ExameNaturezaDto>();

    if(exameNaturezaId !== null) {
      this.alteracaoExameLiberada = false;

      exame$ =
      this.exameNaturezaService.consultarNatureza(exameNaturezaId)
      .pipe(
        tap((dto) => {
        this.modelo.controls.exameNaturezaId.setValue(dto.id);
        this.modelo.controls.exameNaturezaTexto.setValue(dto.natureza);
       })
      );
    }

    exame$
    .pipe(
      switchMap(() => this.pacienteService.consultarPacienteEmCache()),
      tap((paciente) => {
        this.modelo.controls.pessoaId.setValue(paciente.id);
        this.modelo.controls.pessoaNome.setValue(paciente.nome);
      }),
      finalize(() => {
        this.emOperacao = false;
      })
    )
    .subscribe(() => { });
  }

  carregar(dto: PessoaExamePeriodicidadeConsultaDto) {
    this.modelo.setValue(dto);      
  }

  salvar() {
    this.submetido = true;
    console.log(this.modelo);
    if(!this.modelo.valid) {
      this.toast.showWarning('Favor preencher todos os campos obrigatórios.');
      return;
    }

    this.emOperacao = true;

    this.pessoaExamePeriodicidadeService.salvar(this.gerarDto())
    .pipe(
      catchError((err) => {
        this.toast.showError('Ops, ocorreu um problema ao salvar a periodicidade.');
        this.emOperacao = false;
        throw err;
      })
    )
    .subscribe((dto) => {
      this.submetido = false;
      this.emOperacao = false;
      this.carregar(dto);
      this.toast.showSuccess('Periodicidade salva com sucesso.');
    });
  }

  gerarDto(): PessoaExamePeriodicidadeInsercaoDto | PessoaExamePeriodicidadeInsercaoDto {
    let insercao: PessoaExamePeriodicidadeInsercaoDto = {
      pessoaId: this.modelo.controls.pessoaId.value,
      exameNaturezaId: this.modelo.controls.exameNaturezaId.value,
      periodicidadeId: this.modelo.controls.periodicidadeId.value,
      motivo: this.modelo.controls.motivo.value,
      ativo: this.modelo.controls.ativo.value
    };

    if(this.modelo.controls.id.value > 0) {
      let edicao = { ...insercao, id: this.modelo.controls.id.value };
      return edicao;
    }

    return insercao;
  }

  fechandoModal(args: CloseEventArgs) {
    if(this.modelo.controls.id.value > 0) {
      this.assuntoCadastro.next(this.modelo.controls.periodicidadeId.value);
    }

    this.assuntoCadastro.next(null);
  }

  exameNaturezaSelecionado(exame: ExameNaturezaDto) {
    this.modelo.controls.exameNaturezaId.setValue(exame.id);
    this.modelo.controls.exameNaturezaTexto.setValue(exame.natureza);
  }
}



